import { useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion, useReducedMotion, type DragControls } from "motion/react";
import {
  AlertTriangle,
  Bug,
  ClipboardCheck,
  Gauge,
  Maximize2,
  Minimize2,
  Minus,
  RotateCcw,
  Send,
  Server,
  ShieldCheck,
  Sparkles,
  Square,
  X,
  type LucideIcon,
} from "lucide-react";
import { cn } from "../../ui/utils";
import { AssistantMascot } from "./AssistantMascot";
import { MessageBubble, TypingIndicator } from "./MessageBubble";
import { QUICK_ACTIONS, SUGGESTED_PROMPTS } from "../lib/responseEngine";
import { SuggestionChip } from "./SuggestionCard";
import { useComplianceAssistantStore } from "../store";
import type { ChatMessage } from "../types";

const MAX_CHARS = 1000;

const ACTION_ICONS: Record<string, LucideIcon> = {
  gauge: Gauge,
  bug: Bug,
  server: Server,
  clipboard: ClipboardCheck,
  alert: AlertTriangle,
  shield: ShieldCheck,
};

function HeaderButton({
  label,
  onClick,
  children,
}: {
  label: string;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={label}
      aria-label={label}
      className="flex size-11 cursor-pointer items-center justify-center rounded-lg text-white/80 transition-[color,background-color,transform] duration-150 hover:bg-white/15 hover:text-white active:scale-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70 motion-reduce:active:scale-100 sm:size-8"
    >
      {children}
    </button>
  );
}

function PoweredByRego() {
  return (
    <span className="flex items-center gap-1 text-[0.6875rem] font-medium text-muted-foreground">
      Powered by
      <svg
        width="14"
        height="14"
        viewBox="0 0 32 32"
        fill="none"
        aria-hidden
        className="shrink-0"
      >
        <defs>
          <linearGradient id="ca-shield-grad-1" x1="0%" y1="100%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#A18DC2" />
            <stop offset="50%" stopColor="#BAC3E3" />
            <stop offset="100%" stopColor="#A18DC2" />
          </linearGradient>
          <linearGradient id="ca-shield-grad-2" x1="100%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#CE7EB3" />
            <stop offset="50%" stopColor="#D6A0C8" />
            <stop offset="100%" stopColor="#A090C4" />
          </linearGradient>
        </defs>
        <path
          d="M16 30C11.5 28.6 8 24.8 6.5 20.5C5.6 18.2 5.1 15.7 4.8 13.2C4.8 12.6 4.4 6.4 4.7 6.4C8.7 6.3 12.6 5.3 16 3"
          fill="url(#ca-shield-grad-1)"
        />
        <path
          d="M16 30C20.5 28.6 24 24.8 25.5 20.5C26.4 18.2 26.9 15.7 27.2 13.2C27.2 12.6 27.6 6.4 27.3 6.4C23.3 6.3 19.4 5.3 16 3"
          fill="url(#ca-shield-grad-2)"
        />
        <path
          d="M4.7 6.4C4.7 6.4 7.2 6.4 10 5.7C10 5.7 10.1 18 16 21.5V30C16 30 4.6 27.7 4.6 8C4.6 6.3 4.7 6.4 4.7 6.4Z"
          fill="url(#ca-shield-grad-1)"
          opacity="0.7"
        />
        <path
          d="M27.3 6.4C26.7 6.3 24.9 6.4 22 5.7C22 5.7 21.9 18 16 21.5V30C16 30 27.5 27.4 27.4 8C27.4 7.9 27.4 7.8 27.4 7.7C27.4 6.4 27.3 6.4 27.3 6.4Z"
          fill="url(#ca-shield-grad-2)"
          opacity="0.7"
        />
        <path
          d="M22.5 11.5L22.6 11.7C21.2 13 19.9 14.4 18.7 15.9C17 18.3 15.4 21 14 23.8H13.8L9.8 18.9L9.9 18.7H10L13.4 20.6C14.8 18.7 16.3 16.8 18 15.2C18.8 14.4 19.6 13.7 20.4 13C21.2 12.4 21.9 11.9 22.5 11.5Z"
          fill="white"
        />
      </svg>
      <svg width="40" height="14" viewBox="0 0 80 24" fill="none" aria-hidden className="shrink-0">
        <defs>
          <linearGradient id="ca-text-grad" x1="0%" y1="100%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#A1A6D3" />
            <stop offset="50%" stopColor="#9FA0CF" />
            <stop offset="80%" stopColor="#A097C9" />
            <stop offset="100%" stopColor="#A089C0" />
          </linearGradient>
        </defs>
        <text
          x="0"
          y="20"
          fill="url(#ca-text-grad)"
          fontFamily="'Montserrat', sans-serif"
          fontWeight="600"
          fontSize="20"
          letterSpacing="-0.5"
        >
          RegO
        </text>
      </svg>
    </span>
  );
}

const CAPABILITIES: Array<{ icon: LucideIcon; text: string }> = [
  { icon: Gauge, text: "Check compliance posture and scores" },
  { icon: ShieldCheck, text: "Explore controls and frameworks" },
  { icon: Sparkles, text: "Surface insights and next steps" },
];

function EmptyState({ onSend }: { onSend: (prompt: string) => void }) {
  const reduceMotion = useReducedMotion();

  const container = reduceMotion
    ? undefined
    : {
        hidden: {},
        show: { transition: { staggerChildren: 0.045, delayChildren: 0.05 } },
      };
  const item = reduceMotion
    ? undefined
    : {
        hidden: { opacity: 0, y: 10 },
        show: { opacity: 1, y: 0, transition: { duration: 0.4, ease: [0.16, 1, 0.3, 1] as const } },
      };

  return (
    <motion.div
      variants={container}
      initial={reduceMotion ? undefined : "hidden"}
      animate={reduceMotion ? undefined : "show"}
      className="flex flex-col items-center px-2 pt-6 text-center"
    >
      <motion.div variants={item}>
        <AssistantMascot className="size-16" hoverInspect={false} />
      </motion.div>

      <motion.h2 variants={item} className="mt-3 text-base font-bold tracking-tight text-foreground">
        Hi, I'm your Compliance Companion
      </motion.h2>
      <motion.p variants={item} className="mt-1.5 max-w-[19rem] text-[0.8125rem] leading-relaxed text-muted-foreground">
        Ask me anything about your controls, frameworks, and compliance posture.
      </motion.p>

      <motion.ul variants={item} className="mt-5 w-full max-w-[19rem] space-y-2 text-left">
        {CAPABILITIES.map(({ icon: Icon, text }) => (
          <li key={text} className="flex items-center gap-2.5 text-[0.75rem] text-foreground/80">
            <span className="flex size-6 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary dark:bg-primary/15">
              <Icon className="size-3.5" strokeWidth={2} />
            </span>
            {text}
          </li>
        ))}
      </motion.ul>

      <motion.div variants={item} className="mt-6 w-full">
        <p className="mb-2.5 flex items-center justify-center gap-1.5 text-[0.6875rem] font-semibold tracking-wider text-muted-foreground">
          <Sparkles className="size-3.5 text-primary" />
          Try asking
        </p>
        <div className="flex flex-wrap justify-center gap-1.5">
          {QUICK_ACTIONS.map((action) => (
            <SuggestionChip
              key={action.id}
              icon={ACTION_ICONS[action.icon ?? ""]}
              title={action.label}
              onClick={() => onSend(action.prompt)}
            />
          ))}
          {SUGGESTED_PROMPTS.map((p, i) => (
            <SuggestionChip key={`p-${i}`} title={p} onClick={() => onSend(p)} />
          ))}
        </div>
      </motion.div>
    </motion.div>
  );
}

export function ChatPanel({ dragControls }: { dragControls?: DragControls }) {
  const open = useComplianceAssistantStore((s) => s.open);
  const minimized = useComplianceAssistantStore((s) => s.minimized);
  const messages = useComplianceAssistantStore((s) => s.messages);
  const isThinking = useComplianceAssistantStore((s) => s.isThinking);
  const progress = useComplianceAssistantStore((s) => s.progress);
  const sendMessage = useComplianceAssistantStore((s) => s.sendMessage);
  const stopGeneration = useComplianceAssistantStore((s) => s.stopGeneration);
  const retry = useComplianceAssistantStore((s) => s.retry);
  const resetConversation = useComplianceAssistantStore((s) => s.resetConversation);
  const closePanel = useComplianceAssistantStore((s) => s.closePanel);
  const hidePanel = useComplianceAssistantStore((s) => s.hidePanel);
  const minimizePanel = useComplianceAssistantStore((s) => s.minimizePanel);
  const maximizePanel = useComplianceAssistantStore((s) => s.maximizePanel);

  const [input, setInput] = useState("");
  const scrollRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const pinnedRef = useRef(true);
  // Whether a real conversation has started. Until it does, the message area
  // shows the welcome/empty state pinned to the TOP rather than auto-scrolling
  // down to the suggestion chips.
  const hasConvoRef = useRef(false);

  const hasConversation = messages.length > 0;

  // Single polite live region for screen readers: announces that a reply is in
  // progress, then the finished answer as plain text. Keeps the token-by-token
  // reveal (a purely visual effect) from being announced character by character.
  const liveRegionText = useMemo(() => {
    if (isThinking) return "Compliance Companion is responding";
    for (let i = messages.length - 1; i >= 0; i--) {
      const m = messages[i];
      if (m.role !== "assistant") continue;
      if (!m.text) return "";
      return m.text
        .replace(/```[\s\S]*?```/g, " chart. ")
        .replace(/\[([^\]]+)\]\([^)]*\)/g, "$1")
        .replace(/[#*_`>|]/g, " ")
        .replace(/\s+/g, " ")
        .trim();
    }
    return "";
  }, [isThinking, messages]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    hasConvoRef.current = messages.length > 0;
    // With a conversation, follow the latest reply; on the empty/welcome state,
    // keep the welcome message in view rather than the suggestions below it.
    el.scrollTop = messages.length > 0 ? el.scrollHeight : 0;
  }, [messages.length, isThinking]);

  useEffect(() => {
    const el = scrollRef.current;
    const content = el?.firstElementChild;
    if (!el || !content) return;

    const onScroll = () => {
      pinnedRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
    };
    el.addEventListener("scroll", onScroll, { passive: true });

    const ro = new ResizeObserver(() => {
      if (pinnedRef.current && hasConvoRef.current) el.scrollTop = el.scrollHeight;
    });
    ro.observe(content);

    return () => {
      el.removeEventListener("scroll", onScroll);
      ro.disconnect();
    };
  }, []);

  useEffect(() => {
    if (open && !minimized) {
      textareaRef.current?.focus();
    }
  }, [open, minimized]);

  const resetTextareaHeight = () => {
    if (textareaRef.current) textareaRef.current.style.height = "auto";
  };

  const handleSend = () => {
    const value = input.trim();
    if (!value || isThinking || value.length > MAX_CHARS) return;
    sendMessage(value);
    setInput("");
    resetTextareaHeight();
  };

  const handleQuickSend = (prompt: string) => {
    if (isThinking) return;
    sendMessage(prompt);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // isComposing guards against Enter committing an in-progress IME candidate
    // (CJK and other composition input) instead of inserting the character.
    if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      handleSend();
    }
  };

  const overLimit = input.length > MAX_CHARS;
  const nearLimit = input.length > MAX_CHARS * 0.9;
  const canSend = input.trim().length > 0 && !isThinking && !overLimit;

  return (
    <div className="flex h-full flex-col overflow-hidden bg-white dark:bg-gradient-to-b dark:from-[#211c42] dark:via-[#171430] dark:to-[#110f22]">
      <div
        onPointerDown={(e) => {
          if (!minimized) return;
          if ((e.target as HTMLElement).closest("button")) return;
          dragControls?.start(e);
        }}
        className={cn(
          "relative flex shrink-0 items-center justify-between gap-2 border-b border-white/20 bg-gradient-to-br from-indigo-600 via-violet-600 to-violet-500 px-4 py-3 dark:border-white/10 dark:from-indigo-600/85 dark:via-violet-600/80 dark:to-fuchsia-600/65",
          minimized && "cursor-grab touch-none select-none active:cursor-grabbing",
        )}
      >
        <span
          aria-hidden
          className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/40 to-transparent"
        />
        <div className="relative flex min-w-0 flex-1 items-center gap-2.5">
          <AssistantMascot
            className="size-9 shrink-0 drop-shadow-[0_2px_4px_rgba(0,0,0,0.25)]"
            inspect={isThinking}
            hoverInspect={false}
          />
          <div className="min-w-0">
            <p className="truncate text-[0.875rem] font-bold leading-tight text-white">
              Compliance Companion
            </p>
            <p className="flex items-center gap-1.5 text-[0.6875rem] text-white/85">
              <span className="relative flex size-1.5">
                <span className="absolute inline-flex size-full animate-ping rounded-full bg-emerald-300 opacity-75 motion-reduce:animate-none" />
                <span className="relative inline-flex size-1.5 rounded-full bg-emerald-300" />
              </span>
              {isThinking ? "Thinking..." : "Online"}
            </p>
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-0.5">
          {hasConversation && (
            <HeaderButton label="Reset conversation" onClick={resetConversation}>
              <RotateCcw className="size-4" />
            </HeaderButton>
          )}
          {minimized ? (
            <HeaderButton label="Expand" onClick={maximizePanel}>
              <Maximize2 className="size-4" />
            </HeaderButton>
          ) : (
            <HeaderButton label="Compact" onClick={minimizePanel}>
              <Minimize2 className="size-4" />
            </HeaderButton>
          )}
          <HeaderButton label="Minimize" onClick={hidePanel}>
            <Minus className="size-4" />
          </HeaderButton>
          <HeaderButton label="Close" onClick={closePanel}>
            <X className="size-4" />
          </HeaderButton>
        </div>
      </div>

      <div className="sr-only" aria-live="polite" aria-atomic="true">
        {liveRegionText}
      </div>

      <div
        ref={scrollRef}
        className="scrollbar-neutral min-h-0 flex-1 overflow-y-auto [mask-image:linear-gradient(to_bottom,black_calc(100%-1.25rem),transparent)]"
      >
        <div className="space-y-6 px-4 pb-8 pt-5">
          {hasConversation ? (
            <>
              {messages.map((m: ChatMessage, i) => (
                <MessageBubble
                  key={m.id}
                  message={m}
                  isLast={i === messages.length - 1}
                  onSend={handleQuickSend}
                  onRetry={(msg) => retry(msg.id)}
                />
              ))}
              <AnimatePresence>
                {isThinking && <TypingIndicator key="ca-typing" progress={progress} />}
              </AnimatePresence>
            </>
          ) : (
            <EmptyState onSend={handleQuickSend} />
          )}
        </div>
      </div>

      <div className="shrink-0 px-3 pb-3 pt-1">
        <div
          className={cn(
            "relative flex items-end rounded-2xl border shadow-sm transition-all duration-200",
            "border-border/70 bg-card/70 backdrop-blur",
            "focus-within:border-primary/50 focus-within:shadow-md focus-within:ring-4 focus-within:ring-primary/10",
            "dark:border-white/10 dark:bg-white/[0.05] dark:focus-within:border-violet-300/40",
            overLimit &&
              "border-red-400/70 focus-within:border-red-400/70 focus-within:ring-red-500/10 dark:border-red-400/60",
          )}
        >
          <textarea
            ref={textareaRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            onInput={(e) => {
              const t = e.currentTarget;
              t.style.height = "auto";
              t.style.height = `${Math.min(t.scrollHeight, 140)}px`;
            }}
            rows={1}
            maxLength={MAX_CHARS}
            aria-label="Message the Compliance Companion"
            placeholder="Ask a compliance question"
            /* 16px on mobile prevents iOS Safari from auto-zooming the viewport
               on focus; compact 13px from sm up. */
            className="max-h-[140px] min-h-[44px] w-full resize-none border-none bg-transparent py-3 pl-4 pr-14 text-base leading-relaxed text-foreground outline-none placeholder:text-muted-foreground focus:ring-0 sm:text-[0.8125rem]"
          />
          <div className="absolute bottom-2 right-2">
            <motion.button
              type="button"
              onClick={isThinking ? stopGeneration : handleSend}
              disabled={isThinking ? false : !canSend}
              aria-label={isThinking ? "Stop generating" : "Send message"}
              title={
                isThinking
                  ? "Stop generating"
                  : overLimit
                    ? "Message exceeds the character limit"
                    : "Send message"
              }
              whileHover={isThinking || canSend ? { scale: 1.06 } : undefined}
              whileTap={isThinking || canSend ? { scale: 0.9 } : undefined}
              transition={{ type: "spring", stiffness: 420, damping: 17 }}
              className="group/send flex size-9 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-indigo-600 via-violet-600 to-violet-500 text-white shadow-[0_4px_12px_-3px_rgba(124,58,237,0.55)] transition-shadow hover:shadow-[0_6px_16px_-3px_rgba(139,92,246,0.6)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:cursor-not-allowed disabled:from-slate-300 disabled:via-slate-300 disabled:to-slate-300 disabled:text-white/70 disabled:shadow-none dark:disabled:from-white/10 dark:disabled:via-white/10 dark:disabled:to-white/10 dark:disabled:text-white/40"
            >
              {isThinking ? (
                <Square className="size-3.5 fill-current" />
              ) : (
                <Send className="size-4 transition-transform duration-200 group-hover/send:-translate-y-0.5 group-hover/send:translate-x-0.5" />
              )}
            </motion.button>
          </div>
        </div>
        <div className="relative mt-2 flex items-center justify-center px-1">
          <PoweredByRego />
          {/* Counter only surfaces as the limit approaches, so the footer stays
              clean and centered during normal typing. */}
          {nearLimit && (
            <span
              className={cn(
                "absolute right-1 shrink-0 text-[0.6875rem] font-medium tabular-nums",
                overLimit ? "text-red-500" : "text-muted-foreground",
              )}
            >
              {input.length}/{MAX_CHARS}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
